import { EventEmitableService, makeServiceEvent, Service, ServiceEventsToUnion } from 'rainbow-web-sdk/services/service';
import { RBEvent } from '../../models';
import { Observable, Subject, Subscription } from 'rxjs';

export const SETTINGS_SVC = 'SettingsService';
export interface LanguageData {
    label: string;
    settingCode: string;
    code: string;
    isoCode: string;
    codeMoment: string;
    codeDateFns: string;
    unicode: string;
}

export interface SettingsServiceEvents {
    SETTINGS_UPDATED: {
        name: string;
        value: string;
    } | undefined;
    LANGUAGE_UPDATED: string;
}

export class SettingsService extends Service implements EventEmitableService<SettingsServiceEvents> {

    //private redirectVoIP: boolean = false;
    private automaticHookOff: boolean = false;
    private automaticHookOffTime: number = 0;
    private timers: any[] = [];

    private languageCodes: string[] = [];
    private defaultLanguage: LanguageData = { label: "English", settingCode: "en", code: "en", isoCode: "en", codeMoment: "en", codeDateFns: "en-US", unicode: "1f1ec-1f1e7" };
    private languages: LanguageData[] = [
        this.defaultLanguage,
        { label: "Français", settingCode: "fr", code: "fr", isoCode: "fr", codeMoment: "fr", codeDateFns: "fr", unicode: "1f1eb-1f1f7" },
        { label: "Català", settingCode: "ca", code: "ca", isoCode: "ca", codeMoment: "ca", codeDateFns: "ca", unicode: "1f1ea-1f1f8" },
        { label: "Czech", settingCode: "cs", code: "cs", isoCode: "cs", codeMoment: "cs", codeDateFns: "cs", unicode: "1F1E8-1F1FF" },
        { label: "Deutsch", settingCode: "de", code: "de", isoCode: "de", codeMoment: "de", codeDateFns: "de", unicode: "1f1e9-1f1ea" },
        { label: "Español", settingCode: "es-es", code: "es-ES", isoCode: "es-ES", codeMoment: "es", codeDateFns: "es", unicode: "1f1ea-1f1f8" },
        { label: "Suomi", settingCode: "fi", code: "fi", isoCode: "fi", codeMoment: "fi", codeDateFns: "fi", unicode: "1f1eb-1f1ee" },
        { label: "Italiano", settingCode: "it", code: "it", isoCode: "it", codeMoment: "it", codeDateFns: "it", unicode: "1f1ee-1f1f9" },
        { label: "עברית", settingCode: "he", code: "he", isoCode: "he", codeMoment: "he", codeDateFns: "he", unicode: "1f1ee-1f1f1" },
        { label: "日本語", settingCode: "ja", code: "ja", isoCode: "ja", codeMoment: "ja", codeDateFns: "ja", unicode: "1f1ef-1f1f5" },
        { label: "한국어", settingCode: "ko", code: "ko", isoCode: "ko", codeMoment: "ko", codeDateFns: "ko", unicode: "1f1f0-1f1f7" },
        { label: "Nederlands", settingCode: "nl", code: "nl", isoCode: "nl", codeMoment: "nl", codeDateFns: "nl", unicode: "1F1F3-1F1F1" },
        { label: "Nynorsk", settingCode: "no", code: "no", isoCode: "no", codeMoment: "nn", codeDateFns: "nn", unicode: "1F1F3-1F1F4" },
        { label: "Português do Brasil", settingCode: "pt-br", code: "pt-BR", isoCode: "pt-BR", codeMoment: "pt-br", codeDateFns: "pt-BR", unicode: "1F1E7-1F1F7" },
        { label: "Português", settingCode: "pt-pt", code: "pt-PT", isoCode: "pt-PT", codeMoment: "pt", codeDateFns: "pt", unicode: "1F1F5-1F1F9" },
        { label: "Polski", settingCode: "pl", code: "pl", isoCode: "pl", codeMoment: "pl", codeDateFns: "pl", unicode: "1F1F5-1F1F1" },
        { label: "Pусский", settingCode: "ru", code: "ru", isoCode: "ru", codeMoment: "ru", codeDateFns: "ru", unicode: "1F1F7 1F1FA" },
        { label: "Svenskt", settingCode: "sv-se", code: "sv-SE", isoCode: "sv-SE", codeMoment: "sv", codeDateFns: "sv", unicode: "1F1F8-1F1EA" },
        { label: "Türkçe", settingCode: "tr", code: "tr", isoCode: "tr", codeMoment: "tr", codeDateFns: "tr", unicode: "1f1f9-1f1f7" },
        { label: "中文", settingCode: "zh-cn", code: "zh-CN", isoCode: "zh-CN", codeMoment: "zh-cn", codeDateFns: "zh-CN", unicode: "1f1e8-1f1f3" },
        { label: "繁體中文", settingCode: "zh-tw", code: "zh-TW", isoCode: "zh-TW", codeMoment: "zh-tw", codeDateFns: "zh-TW", unicode: "1F1F9-1F1FC" },
        { label: "العربية", settingCode: "ar", code: "ar", isoCode: "ar", codeMoment: "ar", codeDateFns: "ar", unicode: "0600-06FF" }
    ];

    public settings: Record<string, any> = {
        init: true,
        lang: "",
        imSound: "true",
        imNotif: "true",
        displayOrder: "FL",
        nbMaxConversations: "100",
        microphone: "default",
        headsetMicrophone: null,
        speakerphoneMicrophone: null,
        customMicrophone: null,
        headsetDeviceName: null,
        speakerphoneDeviceName: null,
        speaker: "default",
        headsetSpeaker: null,
        speakerphoneSpeaker: null,
        customSpeaker: null,
        ringingSpeaker: "default",
        ringingSpeakerName: "",
        camera: "default",
        cameraUsed: "",
        audioProfile: "handfree",
        currentDevice: null,
        iceConfig: null,
        sdk: false,
        userWiewOrderType: "lastname",
        userWiewFilterType: "all",
        autoStartAnimatedGif: "false",
        animatedEmojis: "false",
        fixedLayout: "false",
        theme: "default",
        translationMsg: "false",
        newUI: "false",
        autoAcceptRoomInvite: "false",
        server: null,
        activeAlarm: "relax1",
        activeNotif: "stairs",
        ringingOnDnd: false,
        devMode: "false",
        hasSecondRinger: "false",
        validationMode: "false",
        disableCalendarPresence: "false",
        deviceList: "[]",
        displayFilesType: "filesListCell--as-grid",
        displayRoomsAsGrid: "true",
        conversationsDensity: "default",
        collapsedButton: "false",
        displayCollapsedConversations: "false",
        handFreeInputDeviceName: null,
        handFreeOutputDeviceName: null,
        customSpeakerName: null,
        customMicrophoneName: null,
        showBodyMessage: "false",
        showBodyChart: "false",
        bubblesFilterType: "all",
        bubblesOrderType: "lastActivityDate",
        webinarsFilterType: 'all',
        webinarsOrderType: 'date',
        protectionAgainstMailTypeOffline: false,
        enableKeyboardLog: "false",
        searchFilterValue: "NoFiltering",
        enableSingleSignOnWebAuthPopup: "false", // IdP login page will appear in a window popup, even in web browser.
        enableSingleSignOnConfig: "false", // Allow SSO configuration even if COMPANY_SINGLE_SIGN_ON_SAML feature is not set in profile.
        extension: "false",
        enableDSCP: false, // Enable DSCP tagging for webrtc packages
        filterIce: false, // Filter prfxl ICE candidates,
        dtx: false, // Discontinuous Transmission (DTX)
        debugWebRTC: false, // Enable debug mode in WebRTC (with several options in conference call)
        sipWiseMode: "false", // SipWise mode
        fontSize: "62.5%",
        lastInactiveBubbleShownDate: null,
        filesOrderBy: "date",
        filesFilterBy: "all",
        filesSortBy: "descending",
        filesGroupBy: "day",
        enterKeyAction: "enterSend",
        activeTalker: false,
        forceConsoleLog: false,
        fullDebugMode: false,
        sfuJoinBundle: false,
        geoip: "", //force IP for get ICE servers geolocation API
        applyCalendarPresence: false,
        informationImSound: "ding",
        importantImSound: "important",
        emergencyImSound: "emergency",
        showNewAnalytics: false,
        sfuCustomMuteMode: false,
        ccdPhoneTypeSelected: "computer",
        ccdProACDNumber: "",
        ccdOtherPhoneNumber: "",
        ccdGroupNumber: "",
        p2pWebrtcStats: "false",
        p2pShowDropAudioBtn: "false",
        p2pWebrtcParameters: "false",
        sfuWebrtcStats: "false",
        sfuShowDropAudioBtn: "false",
        sfuShowCallTools: "false",
        enableDebugMode: 'false',
        analyticsGUIConfig: null,
        sfuDisableVideos: false,
        sfuRecording: "false",
        virtualBackgroundFlag: "false",
        virtualBackgroundType: "none",
        speechEnhancementType: "none",
        virtualBackgroundMirror: "true",
        testConfigEnabled: false,
        imgVirtualBackground: "",
        autoAnswer: false,
        autoAnswerByDeviceType: "",
        enablePBXDisplayForAllCases: "false",
        callLogsViewMode: "condensed",
        subLevelV3: "false",
        enableIceRestartOnHold: "false",
        enableIceRestartOnUnhold: "false",
        disableWaitingCallTone: "false",
        subtitles: "false",
        isAutomaticHookOff: "false",
        automaticHookOffTime: "0",
        customNotificationDelay: "false",
        customNotificationDelayValue: "4000",
        joinWithoutAudio: "false",
        pmode: "false",
        playMessagingNotifOnSecondaryRinger: "false",
        toneOnSecondaryRingerOnly: "false",
        applyMsTeamsPresence: false,
        autoPopoutMode: false,
        openPopoutOnAnswerCall: false,
        openPopoutOnMinimize: false,
        endCallOnClosePopout: false,
        showLastConversation: false,
        allowToCreateManagerAssistant: "true",
        huntingGroupAnalyticSelectedSummary: 1,
        huntingGroupAnalyticsIncomingSectionSelected: "true",
        huntingGroupAnalyticsSelectedCallType: 0,
        huntingGroupAnalyticsChartNumberSelected: 1,
        oldUnreadStyle: "false",
        unreadStyle: "red-line", // "red-line" | "blue-line" | "box",
        displayGroupCall: "true",
        displaySpeedDial: "true",
        displayAllSpeedDial: "true",
        displayFavorites: "true",
        displayManAssist: "true",
        displayAllConversations: "true",
        displayAllActiveConversations: "true",
        displayAllActiveWebinar: "true",
        teamsRemoteControlPluginEnabled: "true",
        teamsRemoteControlLogsEnabled: "true",
        webview2RemoteControlLogsEnabled: "false",
        webview2PluginEnabled: "false",
        letItSnow: "true",
        forceSnow: "false",
        forceRedCodec: "false",
        remoteRpcJid: "",
        remoteRpcResponse: "",
        enableSpeechEnhancement: "false",
        VMAnnounceCusto: "true",
        forwardOnCloudIssue: "false",
        isActivatedWithdrawByUser: "false",
        externalWindowX: "-9999",
        externalWindowY: "-9999",
        externalWindowBorder: "-9999",
        isSoftphone: "false",
        hiddenIsSoftphone: "false",
        disableBusyOverflowFlag: "false",
        manualAleDeviceSelection: "false",
        showRingingSupervisedCalls: "false",
        forceEnableOxeSecretIdentity: "false",
        displaySecretIdentityService: "false",
        incomingCallPopupEnabled: "true",
        incomingCallAutoAnswerEnabled: "false",
        webinarEnableNewSubscriptionMode: "true",
        flagOverflowToVoiceMessage: "true", //RQRAINB-9187
        distributionChannelName: "",
        autoGainControlFlag: "true",
        noiseSuppression: "",
        echoCancellation: "",
        searchBubblesByName: "true",
        searchBubblesByTags: "false",
        searchBubblesByParticipants: "true",
        searchPeopleByName: "true",
        searchPeopleByTags: "false",
        takeSecondCallWhileInConf: "false",
        alwaysMinimized: "false",
        rejectOnBusy: "false",
        developerModeEnabled: "false",
        showOnBoardingContextObjectives: "true",
        maxTargetOnBoarding: "0",
        groupAnalyticRangeType: 'last2days',
        groupAnalyticFrom: null,
        groupAnalyticTo: null,
        groupAnalyticMembersRangeType: 'last2days',
        groupAnalyticMembersFrom: null,
        groupAnalyticMembersTo: null,
        supervisionConsoleViewMode: "normal",
        capabilityWRGConvShift: "false",
        splashScreenCustomActivated: "false",
        splashScreenCustom: "",
        transcription: "false", //MEVO transcription flag
        notificationPosition: "",
        alertSoundLevel: "0.4",
        alertSoundLevelInCall: "0.2",
        conferenceStartConfirmation: "false",
        debugChromium: "", //desktop app only
        Kuando: "", //desktop app only
        modeContrasted: "false",
        enhancedSVGMask: "false",
        modeLinkUnderlined: "false",
        customManifestFileId: "",
        customManifestZipExtract: "",
        userCustomManifest: "",
        userCustomManifestMimetype: "",
        OnlyOfficePhoneRing: false,
        notificationOnSameScreen: "",
        liveTranscription: "false", //AI stuff
        botRAG: "false", //AI stuff
        botRagJid: "", //AI stuff
        translateDocuments: "false", //AI stuff
        liveTranscriptionInputLanguage: "", //AI stuff
        pbxNoiseSuppression: "",
        pbxEchoCancellation: "",
        storeRecentlyUsedReactions: "false",
        showExpandedSupervisionGroups: "",
        customWidgetsOpenStatus: "",
        showSearchDupContacts: "false",
        contactsOrderByType: "", // "firstName" | "lastName" | "company",
        captionSettings: "",
        accessibbleTooltips: "false"
    };

    public letItSnowAvailable = false;

    private updateSubject = new Subject<any>();

    public static getInstance() {
        return Service.create(SETTINGS_SVC, SettingsService);
    }

    public static build() {
        return new SettingsService();
    }

    private constructor() {
        super();

        // Handle letItSnow date
        this.letItSnowAvailable = (new Date()).getMonth() === 11;

        // TODO: memory leak (perhaps ???)
        setInterval(() => { this.letItSnowAvailable = (new Date()).getMonth() === 11; }, 3600000);
    }

    public letItSnowActivated(): boolean {
        if (this.letItSnowAvailable) return this.getBooleanSetting('letItSnow');
        return false;
    }

    public configure(): void {

        //this.redirectVoIP = localStorage.getItem("redirectVoIP") === 'true';
        this.languageCodes = this.languages.map((language: any) => { return language.settingCode; });
        const browserLanguage = navigator.languages ? navigator.languages[0] : (navigator.language || (navigator as any).userLanguage);

        // Config data detected in browser localStorage
        const localSettings = this.getLocalStorageSettings();
        if (localSettings.init) {
            Object.keys(localSettings).forEach((key: string) => { if (localSettings[key] !== null) { this.settings[key] = localSettings[key]; } });
            // If no language defined in settings yet, set it by default to the browser language
            // Localstorage must NOT be updated with this value, this will be done afterwards after user has logged in
            // by using the display language selected by the user.
            if (!this.settings.lang) {
                this.settings.lang = this.getMappedLanguage(browserLanguage);
            }
        }

        // No config found in browser localStorage
        else {
            // No default language for the moment, take the browser language
            // In Desktop client case, this browser language can be defined by the installer command line
            // It allows to specify the first login language (Rainbow_Instaler_name.exe /FirstStartLanguage=ar)
            Object.keys(this.settings).forEach((key: string) => { localStorage.setItem(key, this.settings[key]); });

            // Update of language in settings must be done AFTER the global init of localStorage with default
            // values present in 'this.settings'.
            // Localstorage must NOT be updated with this value, this will be done afterwards after user has logged in
            // by using the display language selected by the user.
            this.settings.lang = this.getMappedLanguage(browserLanguage);
        }

        if (localStorage.useOldFilePicker) config.useOldFilePicker = localStorage.useOldFilePicker === 'true';

        // Force speechEnhancementType to none
        this.setSetting('speechEnhancementType', 'none');

        this.sendEvent('LANGUAGE_UPDATED', this.settings.lang);
        this.sendEvent('SETTINGS_UPDATED', undefined);
    }

    public setSetting(key: string, value: string, notify?: boolean): void {
        this.settings[key] = value;
        localStorage.setItem(key, value);
        if (notify) {
            this.sendUpdateEvent(RBEvent.create(key, value));
        }
    }

    public getSetting(key: string): string | undefined { return this.settings[key]; }
    public getBooleanSetting(key: string): boolean { return this.settings[key] === 'true'; }
    public setSettingTemporarly(key: string, value: string, time: number = 300000): void {
        const previousValue = this.settings[key];
        this.settings[key] = value;
        this.timers.push(setTimeout(() => {
            this.settings[key] = previousValue;
        }, time));
    }

    public sendUpdateEvent(event: RBEvent): void { this.updateSubject.next(event); }

    public events<const EventNames extends keyof SettingsServiceEvents>(...eventNames: EventNames[]): Observable<ServiceEventsToUnion<Pick<SettingsServiceEvents, EventNames>>> {
        return makeServiceEvent(this.updateSubject, ...eventNames);
    }

    public sendEvent<EventName extends keyof SettingsServiceEvents>(eventName: EventName, data: SettingsServiceEvents[EventName]): void {
        this.updateSubject.next(RBEvent.create(eventName, data));
    }

    public subscribeUpdateEvent(eventName: string, handler: any): Subscription {
        return this.updateSubject.subscribe((event: RBEvent) => { if (event.name === eventName) { handler(event.data); } });
    }

    private getLocalStorageSettings(): any {
        const settings: any = {};
        Object.keys(this.settings).forEach((key: string) => { settings[key] = localStorage.getItem(key); });

        if (!settings.nbMaxConversations) { settings.nbMaxConversations = this.settings.nbMaxConversations; }
        else {
            // In case the value is less than 50, reset it to 50
            if (parseInt(settings.nbMaxConversations, 10) < 50) { this.setSetting('nbMaxConversations', '50'); }
        }

        if (settings.accessibbleTooltips === null) { this.setSetting('accessibbleTooltips', 'false'); }

        if (settings.headsetMicrophone === "default") { settings.headsetMicrophone = null; }
        if (settings.headsetSpeaker === "default") { settings.headsetSpeaker = null; }
        if (settings.speakerphoneMicrophone === "default") { settings.speakerphoneMicrophone = null; }
        if (settings.speakerphoneSpeaker === "default") { settings.speakerphoneSpeaker = null; }
        if (settings.customMicrophone === "default") { settings.customMicrophone = null; }
        if (settings.customSpeaker === "default") { settings.customSpeaker = null; }

        return settings;
    }

    //public getRedirectVoIP(): boolean { return this.redirectVoIP; }
    public getRedirectVoIP(): boolean { return this.getSetting("ringingOnDnd").toString() === "false"; }//RQRAINB-4880 from server API + CRRAINB-20103
    //public setRedirectVoIP(redirectVoIP: boolean) { this.redirectVoIP = redirectVoIP; localStorage.setItem('redirectVoIP', redirectVoIP.toString()); }//RQRAINB-4880
    public setRedirectVoIP(redirectVoIP: boolean) {
        this.settings.ringingOnDnd = !redirectVoIP;
        localStorage.setItem("ringingOnDnd", (!redirectVoIP).toString());
    }//RQRAINB-4880+CRRAINB-20103

    public getIncomingCallPopupEnabled(): boolean { return this.getSetting("incomingCallPopupEnabled").toString() === "true"; }

    public setIncomingCallPopupEnabled(incomingCallPopupEnabled: boolean) {
        this.settings.incomingCallPopupEnabled = incomingCallPopupEnabled;
        localStorage.setItem("incomingCallPopupEnabled", (incomingCallPopupEnabled).toString());
    }

    public getIncomingCallAutoAnswerEnabled(): boolean { return this.getSetting("incomingCallAutoAnswerEnabled").toString() === "true"; }

    public setIncomingCallAutoAnswerEnabled(incomingCallAutoAnswerEnabled: boolean) {
        this.settings.incomingCallAutoAnswerEnabled = incomingCallAutoAnswerEnabled;
        localStorage.setItem("incomingCallAutoAnswerEnabled", (incomingCallAutoAnswerEnabled).toString());
    }

    public isAutomaticHookOff(): boolean { return this.getSetting("isAutomaticHookOff").toString() === "true"; }
    public setAutomaticHookOff(isActivated: boolean): void { this.setSetting("isAutomaticHookOff", isActivated.toString()); }

    public getAutomaticHookOffTime(): number { return parseInt(this.getSetting("automaticHookOffTime").toString(), 10); }
    public setAutomaticHookOffTime(milliseconds: number): void { this.setSetting("automaticHookOffTime", milliseconds.toString()); }

    public isCustomNotificationDelay(): boolean { return this.getSetting("isCustomNotificationDelay") === "true"; }
    public setIsCustomNotificationDelay(isActivated: boolean): void { this.setSetting("isCustomNotificationDelay", isActivated.toString()); }

    public getCustomNotificationDelayValue(): number { return parseInt(this.getSetting("customNotificationDelayValue"), 10); }
    public setCustomNotificationDelayValue(milliseconds: number): void { this.setSetting("customNotificationDelayValue", milliseconds.toString()); }

    public setIceConfig(iceConfig: string) { this.settings.iceConfig = iceConfig; }

    public getAvailableLanguages(): LanguageData[] { return this.languages; }

    public getAppliLanguage(): LanguageData {
        const language = this.languages.find((lang) => { return lang.settingCode === this.settings.lang; });
        return language ? language : this.defaultLanguage;
    }

    public getAppliLanguageCodeForServer(): string {
        const langArray = this.settings.lang.split("-");
        return langArray.length === 1 ? this.settings.lang : langArray[0] + "-" + langArray[1].toUpperCase();
    }

    public setAppliLangageCodeFromServer(serverCode: string) {
        const langArray = serverCode.split("-");
        let clientCode = langArray[0];
        if (clientCode) { clientCode = clientCode.toLowerCase(); }
        if (langArray.length === 2) { clientCode += ("-" + langArray[1].toLowerCase()); }
        clientCode = this.getMappedLanguage(clientCode);
        clientCode = (this.languageCodes.indexOf(clientCode) >= 0) ? clientCode : "en";
        this.setAppliLanguageCode(clientCode);
    }

    public setAppliLanguageCode(lang: string): void {
        this.setSetting("lang", lang);
        this.sendEvent('LANGUAGE_UPDATED', lang);
    }

    public getBestLanguageForIsoCode(isoCode: string) {
        const language = this.getLanguageByIsoCode(isoCode);
        if (language) return language;

        // Exact language not found: try to find language without country variant (if any)
        if (isoCode.indexOf("-") !== -1) {
            const isoLanguageWithoutCountry = isoCode.split("-")[0]; // Remove country variant
            return this.getLanguageByIsoCode(isoLanguageWithoutCountry);
        }

        // Really not found return default language
        return this.getLanguageByIsoCode("en");
    }

    public getSimpleLanguageCode(isoCode: string) {
        if (!isoCode) return 'en';
        const isoCodeLower = isoCode.toLowerCase();

        // Exact language not found: try to find language without country variant (if any)
        if (isoCodeLower.indexOf("-") !== -1) {
            const isoLanguageWithoutCountry = isoCodeLower.split("-")[0]; // Remove country variant
            return isoLanguageWithoutCountry;
        }
        return isoCodeLower;
    }

    public getLanguageByIsoCode(isoCode: string) {
        return this.languages.find((lang: any) => { return lang.isoCode === isoCode; });
    }

    // Translate the language return by the browser to a language supported by the application
    private getMappedLanguage(codedLanguage: string) {
        if (!codedLanguage) return 'en';
        const codedLang = codedLanguage.toLowerCase();
        switch (codedLang.split("-")[0]) {
            case "ar": return "ar";
            case "ca": return "ca";
            case "cs": return "cs";
            case "de": return "de";
            case "en": return "en";
            case "es": return "es-es";
            case "fi": return "fi";
            case "fr": return "fr";
            case "he": return "he";
            case "it": return "it";
            case "ja": return "ja";
            case "ko": return "ko";
            case "nl": return "nl";
            case "no": return "no";
            case "pt": return codedLang === "pt-br" ? "pt-br" : "pt-pt";
            case "pl": return "pl";
            case "ru": return "ru";
            case "sv": return "sv-se";
            case "tr": return "tr";
            case "zh": return (["zh-cn", "zh-tw"].indexOf(codedLang) >= 0) ? codedLang : "zh-cn";
            default: return "en";
        }
    }

    // custom manifest method to override by Desktop app
    public async getHybridTelephonyCustomizationManifest(): Promise<any | null> {
        return null;
    }

    public async setHybridTelephonyCustomizationManifest(manifest: any, isZipFile: boolean): Promise<void> {
        console.error(manifest);
    }

    public getRejectOnBusy(): boolean { return this.getSetting("rejectOnBusy").toString() === "true"; }
}
